/* ==========================================================================
   Flyout ("fyler") navigation menu behaviour.
   Custom element <flyout-nav> rendered by snippets/flyout-menu.liquid.

   Supports:
     - hover open/close (with small intent delays) on pointer devices
     - click-to-toggle (data-flyout-trigger="click" or touch / no-hover devices)
     - keyboard: ArrowDown opens + focuses first link, Escape closes + restores
       focus, Tab moves naturally and focus leaving an item closes its panel
     - outside-click / outside-tap closes any open panel
     - aria-expanded kept in sync on each trigger
   No external dependencies.
   ========================================================================== */

if (!customElements.get("flyout-nav")) {
  customElements.define(
    "flyout-nav",
    class FlyoutNav extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(
          this.querySelectorAll(".ftf-item--has-panel"),
        );
        if (!this.items.length) return;

        this.openDelay = 60;
        this.closeDelay = 180;
        this._timers = new WeakMap();
        this._mode = this.dataset.flyoutTrigger === "click" ? "click" : "hover";
        this._noHover =
          window.matchMedia &&
          window.matchMedia("(hover: none)").matches;

        this._onDocPointer = this._onDocPointer.bind(this);
        this._onDocKeydown = this._onDocKeydown.bind(this);

        this.items.forEach((item) => this._bindItem(item));

        document.addEventListener("pointerdown", this._onDocPointer, true);
        document.addEventListener("keydown", this._onDocKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener("pointerdown", this._onDocPointer, true);
        document.removeEventListener("keydown", this._onDocKeydown);
        this.items?.forEach((item) => this._cancelTimer(item));
        this._backdrop?.remove();
      }

      /* ---- helpers ----------------------------------------------------- */
      _trigger(item) {
        return item.querySelector(".ftf-trigger");
      }

      _panel(item) {
        return item.querySelector(".ftf-panel");
      }

      _isClickMode() {
        return this._mode === "click" || this._noHover;
      }

      /* ---- wiring ------------------------------------------------------ */
      _bindItem(item) {
        const trigger = this._trigger(item);
        const panel = this._panel(item);
        if (!trigger || !panel) return;

        // Hover intent
        item.addEventListener("mouseenter", () => {
          if (this._isClickMode()) return;
          this._setTimer(
            item,
            () => {
              this.closeAll(item);
              this.open(item);
            },
            this.openDelay,
          );
        });

        item.addEventListener("mouseleave", () => {
          if (this._isClickMode()) return;
          this._setTimer(item, () => this.close(item), this.closeDelay);
        });

        // Click / tap toggle
        trigger.addEventListener("click", (event) => {
          if (!this._isClickMode()) return; // hover mode lets the link navigate
          event.preventDefault();
          if (item.classList.contains("is-open")) {
            this.close(item);
          } else {
            this.closeAll();
            this.open(item);
          }
        });

        // Keyboard on the trigger
        trigger.addEventListener("keydown", (event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            this.closeAll(item);
            this.open(item);
            this._focusFirst(panel);
          } else if (
            (event.key === "Enter" || event.key === " ") &&
            this._isClickMode()
          ) {
            event.preventDefault();
            if (item.classList.contains("is-open")) {
              this.close(item);
            } else {
              this.closeAll();
              this.open(item);
            }
          }
        });

        // Tabbing into the item opens it; leaving it closes it
        item.addEventListener("focusin", () => {
          this._cancelTimer(item);
          this.closeAll(item);
          this.open(item);
        });

        item.addEventListener("focusout", (event) => {
          if (!item.contains(event.relatedTarget)) {
            this._setTimer(item, () => this.close(item), 0);
          }
        });
      }

      /* ---- timers ------------------------------------------------------ */
      _setTimer(item, fn, delay) {
        this._cancelTimer(item);
        this._timers.set(item, window.setTimeout(fn, delay));
      }

      _cancelTimer(item) {
        const timer = this._timers.get(item);
        if (timer) {
          window.clearTimeout(timer);
          this._timers.delete(item);
        }
      }

      /* ---- open / close ------------------------------------------------ */
      open(item) {
        item.classList.add("is-open");
        this._trigger(item)?.setAttribute("aria-expanded", "true");
        this._syncBackdrop();
      }

      close(item) {
        item.classList.remove("is-open");
        this._trigger(item)?.setAttribute("aria-expanded", "false");
        this._syncBackdrop();
      }

      closeAll(except) {
        this.items.forEach((item) => {
          if (item !== except) {
            this._cancelTimer(item);
            this.close(item);
          }
        });
      }

      _focusFirst(panel) {
        const focusable = panel.querySelector(
          'a[href], button:not([disabled])',
        );
        if (focusable) focusable.focus();
      }

      /* ---- backdrop / page scrim --------------------------------------
         A single <body>-level fixed overlay that dims + blurs the page behind
         any open panel. Created lazily and only on desktop (>= 1200px) so the
         mobile drawer is never touched. It reuses the existing outside-click
         close: a click on the scrim lands outside <flyout-nav>, which triggers
         closeAll() and then hides the scrim through _syncBackdrop(). Styling
         (and the >= 1200px gate) lives in flyout-menu.css (.ftf-backdrop). */
      _isDesktop() {
        return (
          !window.matchMedia ||
          window.matchMedia("(min-width: 1200px)").matches
        );
      }

      _ensureBackdrop() {
        if (!this._backdrop || !this._backdrop.isConnected) {
          this._backdrop =
            document.querySelector(".ftf-backdrop") ||
            document.body.appendChild(
              Object.assign(document.createElement("div"), {
                className: "ftf-backdrop",
              }),
            );
          this._backdrop.setAttribute("aria-hidden", "true");
        }
        return this._backdrop;
      }

      _syncBackdrop() {
        const anyOpen = this.items.some((item) =>
          item.classList.contains("is-open"),
        );
        if (anyOpen && this._isDesktop()) {
          this._ensureBackdrop().classList.add("is-active");
        } else if (this._backdrop) {
          this._backdrop.classList.remove("is-active");
        }
      }

      /* ---- document-level listeners ------------------------------------ */
      _onDocPointer(event) {
        if (!this.contains(event.target)) this.closeAll();
      }

      _onDocKeydown(event) {
        if (event.key !== "Escape") return;
        const openItem = this.items.find((item) =>
          item.classList.contains("is-open"),
        );
        if (openItem) {
          this.close(openItem);
          this._trigger(openItem)?.focus();
        }
      }
    },
  );
}
